<template>
  <div class="settings-item">
    <div class="item-info">
      <div class="item-header">
        <label 
          :for="inputId"
          class="item-label"
        >
          {{ setting.label }}
        </label>
        
        <div class="item-badges">
          <Badge
            v-if="setting.experimental"
            variant="warning"
            size="small"
            text="Experimental"
          />
          <Badge
            v-if="setting.deprecated"
            variant="error"
            size="small"
            text="Deprecated"
          />
          <Badge
            v-if="setting.scope === 'workspace'"
            variant="info"
            size="small"
            text="Workspace"
          />
          <Badge
            v-if="setting.scope === 'user'"
            variant="default"
            size="small"
            text="User"
          />
        </div>
      </div>
      
      <p v-if="setting.description" class="item-description">
        {{ setting.description }}
      </p>
      
      <div class="item-meta">
        <code class="item-key">{{ setting.key }}</code>
        <Button
          v-if="hasChanged"
          variant="icon"
          icon="refresh"
          size="small"
          @click="resetToDefault"
          :title="'Reset to default: ' + setting.defaultValue"
        />
      </div>
    </div>
    
    <div class="item-control">
      <!-- Boolean setting -->
      <Toggle
        v-if="setting.type === 'boolean'"
        :id="inputId"
        v-model="localValue"
        :disabled="isDisabled"
        @change="onValueChange"
      />
      
      <!-- String setting -->
      <InputBox
        v-else-if="setting.type === 'string'"
        :id="inputId"
        v-model="localValue"
        :disabled="isDisabled"
        :placeholder="setting.defaultValue"
        @change="onValueChange"
      />
      
      <!-- Number setting -->
      <InputBox
        v-else-if="setting.type === 'number'"
        :id="inputId"
        v-model.number="localValue"
        type="number"
        :disabled="isDisabled"
        :placeholder="setting.defaultValue?.toString()"
        @change="onValueChange"
      />
      
      <!-- Select/Enum setting -->
      <SelectBox
        v-else-if="setting.type === 'select' || setting.type === 'enum'"
        :id="inputId"
        v-model="localValue"
        :options="selectOptions"
        :disabled="isDisabled"
        @change="onValueChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Badge, Button, InputBox, SelectBox, Toggle } from '@/components/atoms';
import type { SettingsItem } from './SettingsPanel.vue';

export interface SettingsItemProps {
  setting: SettingsItem;
  scope: 'user' | 'workspace';
}

const props = defineProps<SettingsItemProps>();

const emit = defineEmits<{
  'update': [key: string, value: any];
}>();

const localValue = ref(props.setting.value ?? props.setting.defaultValue);
const inputId = computed(() => `setting-${props.setting.id}`);

const isDisabled = computed(() => {
  if (props.setting.deprecated) return true;
  if (props.setting.scope && props.setting.scope !== props.scope && props.setting.scope !== 'both') {
    return true;
  }
  return false;
});

const hasChanged = computed(() => {
  return JSON.stringify(localValue.value) !== JSON.stringify(props.setting.defaultValue);
});

const selectOptions = computed(() => {
  if (props.setting.options) {
    return props.setting.options.map(option => ({
      label: option.label,
      value: option.value,
      description: option.description,
    }));
  }
  return [];
});

// Watch for external changes to the setting value
watch(
  () => props.setting.value,
  (newValue) => {
    localValue.value = newValue ?? props.setting.defaultValue;
  }
);

const onValueChange = () => {
  emit('update', props.setting.key, localValue.value);
};

const resetToDefault = () => {
  localValue.value = props.setting.defaultValue;
  onValueChange();
};
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.settings-item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--spacing-xl);
  align-items: start;
  padding: var(--spacing-lg);
  border: 1px solid transparent;
  border-radius: var(--border-radius);
  transition: border-color 0.1s ease, background-color 0.1s ease;
  
  &:hover {
    background-color: var(--vscode-list-hoverBackground);
    border-color: var(--vscode-border);
  }
}

.item-info {
  min-width: 0; // Allow text to wrap and ellipsis
}

.item-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.item-label {
  font-weight: 500;
  color: var(--vscode-foreground);
  cursor: pointer;
  line-height: 1.4;
}

.item-badges {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
}

.item-description {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 14px;
  color: var(--vscode-foreground);
  opacity: 0.8;
  line-height: 1.4;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.item-key {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: var(--vscode-foreground);
  opacity: 0.6;
  background-color: var(--vscode-background);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--vscode-border);
}

.item-control {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 200px;
}

// Responsive layout for smaller screens
@media (max-width: 768px) {
  .settings-item {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
  
  .item-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .item-control {
    justify-content: flex-start;
    min-width: unset;
  }
}
</style>
